/* eslint-disable camelcase */
import React, {
  useState,
  useEffect,
} from 'react';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
} from '@stripe/react-stripe-js';

import {
  shallowEqual,
  useDispatch,
  useSelector,
} from 'react-redux';
import {
  getStatus,
} from 'redux-resource';

import {
  coreActions,
  coreSelectors,
  PaymentMethod,
} from '@gymcloud/core';

import config from 'web-config';

import {
  Button,
  Select,
  Spinner,
  NotFound,
  ContentWrapper,
} from 'web-components';

import {
  Flex,
  Text,
  H300,
  TextBreadcrumbs,
} from 'web-styled';

import CardForm from './CardForm';
import * as S from './styled';

const stripePromise = loadStripe(config.stripe.key);

const mapState = state => ({
  readStatus: getStatus(
    state,
    [
      'products.requests.fetchProducts.status',
      'productPlans.requests.fetchProductPlans.status',
    ],
    true,
  ),
  updateStatus: getStatus(
    state,
    [
      'paymentIntents.requests.buyProduct.status',
      'paymentIntents.requests.finishBuy.status',
      'paymentMethods.requests.create.status',
      'paymentMethods.requests.delete.status',
      'paymentMethods.requests.setDefault.status',
    ],
  ),
  pro: coreSelectors.getClientPro(state),
  profile: coreSelectors.getProfile(state),
  products: coreSelectors.getResourceMappedList(
    'products',
  )(state),
  productPlans: coreSelectors.getResourceMappedList(
    'productPlans',
  )(state),
  paymentMethods: coreSelectors.getResourceMappedList(
    'paymentMethods',
  )(state),
  paymentIntents: coreSelectors.getResourceMappedList(
    'paymentIntents',
  )(state),
  subscriptions: coreSelectors.getSubscriptions(state),
});

const ProductsPage = () => {
  const {
    pro,
    profile,
    products,
    readStatus,
    updateStatus,
    productPlans,
    subscriptions,
    paymentMethods,
    paymentIntents,
  } = useSelector(mapState, shallowEqual);
  const [planId, setPlanId] = useState(null);
  const hasPlans = productPlans?.length > 0;
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(
      coreActions.requestFetchClientEntity({
        clientId: pro?.id,
        resourceType: 'products',
      }),
    );
    dispatch(
      coreActions.requestFetchClientEntity({
        clientId: pro?.id,
        resourceType: 'productPlans',
      }),
    );
  }, []);

  const onBuyProduct = (productId: number) => {
    const onSuccess = (data) => {
      console.log('data', data);
    };

    dispatch(
      coreActions.requestBuyProduct({
        payload: {
          client_id: profile.id,
          product_id: productId,
        },
        callbacks: {
          onSuccess,
        },
      }),
    );
  };

  const onSubscribeProduct = () => {
    const onSuccess = () => {
      dispatch(
        coreActions.requestFetchClientEntity({
          list: 'library',
          resourceType: 'personalWorkouts',
        }),
      );
      dispatch(
        coreActions.requestFetchClientEntity({
          list: 'library',
          resourceType: 'personalPrograms',
        }),
      );
    };
    dispatch(
      coreActions.requestSetPlan({
        body: {
          product_plan_id: planId,
        },
        callbacks: {
          onSuccess,
        },
      }),
    );
  };

  const onDeletePaymentMethod = (method: PaymentMethod) => {
    dispatch(
      coreActions.requestDeletePaymentMethod({
        id: method.id,
      }),
    );
  };

  const onSetDefaultPaymentMethod = (method: PaymentMethod) => {
    dispatch(
      coreActions.requestSetDefaultPaymentMethod({
        id: method.id,
      }),
    );
  };

  const handleSuccess = (res: any) => {
    if (res?.paymentMethod?.id) {
      dispatch(
        coreActions.requestCreatePaymentMethod({
          body: {
            id: res.paymentMethod.id,
          },
        }),
      );
    } else {
      dispatch(
        coreActions.requestFinishBuy({
          paymentIntentId: res.paymentIntent?.id,
        }),
      );
    }
  };

  return (
    <ContentWrapper
      header={(
        <Flex fillSpace align="center" justify="space-between">
          <TextBreadcrumbs>
            Products
          </TextBreadcrumbs>
        </Flex>
      )}
    >
      {readStatus.succeeded && (
        <Flex
          width="100%"
          padding="15px"
          spacing={15}
        >
          <Flex
            column
            width="400px"
            padding="15px"
            spacing={15}
          >
            {products.map(product => (
              <Flex
                column
                spacing={10}
                width="100%"
                key={product.id}
              >
                <Flex column>
                  <H300>
                    {product.name}
                  </H300>
                  <Text>
                    {product.description}
                  </Text>
                </Flex>
                <Button
                  ghost
                  appearance="primary"
                  onClick={() => onBuyProduct(product.id)}
                >
                  Buy
                </Button>
              </Flex>
            ))}

            {hasPlans && (
              <Flex
                column
                spacing={10}
                width="300px"
              >
                <hr />
                <TextBreadcrumbs>
                  Plans
                </TextBreadcrumbs>
                <Select
                  options={productPlans.map(
                    p => ({
                      value: String(p.id),
                      label: `${p.amount} ${p.currency} - ${p.interval_count} ${p.interval}`,
                    }),
                  )}
                  styles={{
                    container: provided => ({
                      ...provided,
                      width: '100%',
                    }),
                    menuList: provided => ({
                      ...provided,
                      maxHeight: '316px',
                    }),
                    control: provided => ({
                      ...provided,
                      boxShadow: 'none',
                      borderColor: 'hsl(0,0%,80%)',
                    }),
                  }}
                  onChange={(item) => {
                    if (item?.value) {
                      setPlanId(item.value);
                    }
                  }}
                />
              </Flex>
            )}

            {planId && (
              <Button
                ghost
                appearance="primary"
                onClick={onSubscribeProduct}
              >
                Subscribe
              </Button>
            )}
          </Flex>

          <Flex
            column
            spacing={15}
          >
            <Flex
              column
              width="100%"
              spacing={10}
            >
              {paymentMethods.map(m => (
                <Flex
                  key={m.id}
                  width="100%"
                  align="center"
                  justify="space-between"
                >
                  <Flex align="center" spacing={8}>
                    <S.CardIcon brand={m.card.brand} />
                    <Text>
                      {m.card.last4}
                      ,
                    </Text>
                    <Text>
                      {m.card.exp_month}
                      /
                      {m.card.exp_year}
                    </Text>
                  </Flex>
                  <Flex align="center" spacing={8}>
                    <Button
                      appearance="link"
                      onClick={() => onSetDefaultPaymentMethod(m)}
                    >
                      Make default
                    </Button>
                    <Button
                      ghost
                      circled
                      icon="fas fa-trash-alt"
                      appearance="danger"
                      onClick={() => onDeletePaymentMethod(m)}
                    />
                  </Flex>
                </Flex>
              ))}
            </Flex>
            <Elements stripe={stripePromise}>
              <CardForm
                clientSecret={paymentIntents[0]?.client_secret}
                onSuccess={handleSuccess}
              />
            </Elements>
            {subscriptions.length > 0 && (
              <Flex
                column
                width="100%"
                spacing={15}
              >
                <hr />
                <TextBreadcrumbs>
                  Subscriptions
                </TextBreadcrumbs>
                <Flex column spacing={5}>
                  {subscriptions.map(s => (
                    <Flex
                      key={s.id}
                      spacing={10}
                    >
                      <Text>
                        {s.plan.name}
                      </Text>
                      <Text>
                        {`${s.plan.amount} ${s.plan.currency}`}
                      </Text>
                    </Flex>
                  ))}
                </Flex>
              </Flex>
            )}
          </Flex>
        </Flex>
      )}

      {readStatus.pending && (
        <Spinner message="Loading Products..." />
      )}

      {readStatus.failed && (
        <NotFound title="Something went wrong" />
      )}

      {updateStatus.pending && (
        <Spinner over />
      )}
    </ContentWrapper>
  );
};

export default ProductsPage;
